import React, { useEffect, useRef, useState } from "react";
import { productsBundleMocks } from "../../mocks/products";
import { BUTTON_TYPE_OPTIONS, CHECKBOX_TYPE_OPTIONS, INPUT_TYPES_OPTIONS, LANGUAGE_OPTIONS, OVERALL_TYPES_OPTIONS, WINDOW_TYPES_OPTIONS } from "~/constants";
import classnames from "classnames";
import styles from "./dev/DevEnvironment.module.scss";
import Input from "~/components/inputs/Input";
import useDetectDivClick from "../../hooks/useDetectDivClick";
import { SUCCESS_CONFETTI_OPTIONS } from "./dev/DevEnvironment.core";
import { toNumber } from "lodash";
import { getOrderIdLabelIds } from "~/cores/brandDataCore";
import { getFontSelectOptions } from "../apps/appUtils/fonts";
import { useDebounce } from "../../hooks/useDebounce";
import CollapsibleContent from "rylak-common/src/components/collapsible/CollapsibleContent.jsx";
import TurnrDropdownSelect from "~/components/inputs/TurnrDropdownSelect.js";

function SettingsIcon({ width = "100%" }) {
    return (
        <svg id="Capa_1" enableBackground="new 0 0 189.524 189.524" viewBox="0 0 189.524 189.524" width={width} xmlns="http://www.w3.org/2000/svg">
            <g>
                <g>
                    <path
                        clipRule="evenodd"
                        d="m56.26 52.119c-2.104 8.174-9.524 14.214-18.355 14.214-10.467 0-18.952-8.485-18.952-18.952s8.485-18.952 18.952-18.952c8.831 0 16.251 6.04 18.355 14.214h128.526c2.616 0 4.738 2.121 4.738 4.738s-2.122 4.738-4.738 4.738zm-8.879-4.738c0 5.234-4.243 9.476-9.476 9.476s-9.476-4.243-9.476-9.476 4.243-9.476 9.476-9.476 9.476 4.242 9.476 9.476z"
                        fillRule="evenodd"
                    />
                </g>
                <g>
                    <path d="m4.738 52.119h14.811c-.39-1.514-.597-3.102-.597-4.738s.207-3.224.597-4.738h-14.811c-2.617 0-4.738 2.121-4.738 4.738s2.121 4.738 4.738 4.738z" />
                </g>
                <g>
                    <path
                        clipRule="evenodd"
                        d="m113.117 137.405c-2.104-8.174-9.525-14.214-18.355-14.214s-16.252 6.04-18.355 14.214h-71.669c-2.617 0-4.738 2.122-4.738 4.738s2.121 4.738 4.738 4.738h71.668c2.104 8.174 9.525 14.214 18.355 14.214s16.252-6.04 18.355-14.214h71.668c2.616 0 4.738-2.122 4.738-4.738s-2.122-4.738-4.738-4.738zm-18.355 14.214c5.234 0 9.476-4.242 9.476-9.476s-4.242-9.476-9.476-9.476-9.476 4.242-9.476 9.476 4.242 9.476 9.476 9.476z"
                        fillRule="evenodd"
                    />
                </g>
                <g>
                    <path
                        clipRule="evenodd"
                        d="m169.974 90.024c-2.104-8.174-9.525-14.214-18.355-14.214s-16.252 6.04-18.355 14.214h-128.526c-2.617 0-4.738 2.122-4.738 4.738s2.121 4.738 4.738 4.738h128.526c2.104 8.174 9.525 14.214 18.355 14.214s16.252-6.04 18.355-14.214h14.811c2.616 0 4.738-2.122 4.738-4.738s-2.122-4.738-4.738-4.738zm-18.355 14.214c5.234 0 9.476-4.242 9.476-9.476s-4.243-9.476-9.476-9.476c-5.234 0-9.476 4.242-9.476 9.476s4.242 9.476 9.476 9.476z"
                        fillRule="evenodd"
                    />
                </g>
            </g>
        </svg>
    );
}

export default function SettingsComponent(props) {
    const { brandName, brandData, setBrandData, additionalSettings } = props;
    const [tempBrandDataValue, setTempBrandDataValue] = useState(null);
    const debouncedBrandDataValue = useDebounce(tempBrandDataValue, 50);
    const [styleEditorExpanded, setStyleEditorExpanded] = useState(false);

    const devComponentRef = useRef(null);

    useDetectDivClick({
        ref: devComponentRef,
        outsideDivCallback: () => {
            setStyleEditorExpanded(false);
        },
    });

    function updateChangedValues(name, value, debounced) {
        if (debounced) {
            setTempBrandDataValue({ name, value });
        } else {
            const newChangedValues = { ...brandData };
            newChangedValues[name] = value;
            setBrandData(newChangedValues);
        }
    }

    useEffect(() => {
        if (debouncedBrandDataValue) {
            updateChangedValues(debouncedBrandDataValue.name, debouncedBrandDataValue.value);
        }
    }, [debouncedBrandDataValue]);

    function getInputBrandInputValue(valueKey) {
        return brandData?.[valueKey] || "";
    }

    function getInputBrandInputValues(valueKey, { type, debounced } = {}) {
        return {
            value: getInputBrandInputValue(valueKey),
            name: valueKey,
            onChange: (v) => updateChangedValues(valueKey, type === "number" ? v && toNumber(v) : v, debounced),
            type,
        };
    }

    return (
        <div ref={devComponentRef} id="turnr-dev-container" className={classnames(styles["wrapper"], styleEditorExpanded && styles["wrapper-expanded"])}>
            <div className={styles["settings-header-wrapper"]} onClick={() => setStyleEditorExpanded(!styleEditorExpanded)}>
                <div className={styles["settings-icon"]}>
                    <SettingsIcon />
                </div>
                <div>
                    <h3 className={styles["settings-header"]}>Brand Settings</h3>
                </div>
            </div>
            <CollapsibleContent isOpen={styleEditorExpanded}>
                <div className={styles["collapsable-content-wrapper"]}>
                    {additionalSettings}
                    <div className={styles["color-wrapper"]}>
                        <div
                            style={{
                                fontSize: "14px",
                                color: "#9b9b9b",
                                marginLeft: "28px",
                            }}
                        >
                            Brand Color
                        </div>
                        <Input
                            name="colorPicker"
                            {...getInputBrandInputValues("colorPrimary", {
                                type: "color",
                                debounced: true,
                            })}
                        />
                    </div>
                    <TurnrDropdownSelect {...getInputBrandInputValues("defaultLanguage")} fullWidth brandData={brandData} label={"Default Language"} options={LANGUAGE_OPTIONS} />
                    <TurnrDropdownSelect {...getInputBrandInputValues("fontNamePrimary")} fullWidth brandData={brandData} label="Font" options={getFontSelectOptions(brandName)} />
                    {/*<Input {...getInputBrandInputValues("fontSizeBody")} brandData={brandData} type={"number"} placeholder="Font size" />*/}
                    <TurnrDropdownSelect {...getInputBrandInputValues("buttonType")} fullWidth brandData={brandData} label="Button type" options={BUTTON_TYPE_OPTIONS} />
                    <TurnrDropdownSelect {...getInputBrandInputValues("inputType")} fullWidth brandData={brandData} label="Input type" options={INPUT_TYPES_OPTIONS} />
                    <TurnrDropdownSelect {...getInputBrandInputValues("checkboxType")} fullWidth brandData={brandData} label="Checkbox type" options={CHECKBOX_TYPE_OPTIONS} />
                    <TurnrDropdownSelect {...getInputBrandInputValues("windowType")} fullWidth brandData={brandData} label="Window type" options={WINDOW_TYPES_OPTIONS} />
                    <TurnrDropdownSelect {...getInputBrandInputValues("overallType")} fullWidth brandData={brandData} label="Overall type" options={OVERALL_TYPES_OPTIONS} />
                    <div className={styles["order-wrapper"]}>
                        <Input {...getInputBrandInputValues("orderIdPrefix")} placeholder="Order Prefix" />
                    </div>
                    <div className={styles["logo-wrapper"]}>
                        <Input {...getInputBrandInputValues("logoSrc")} placeholder="Logo src" />
                        <Input
                            {...getInputBrandInputValues("logoHeight", {
                                type: "number",
                            })}
                            placeholder="Logo height"
                        />
                    </div>
                    <div className={styles["logo-wrapper"]}>
                        <Input {...getInputBrandInputValues("alternativeLogoSrc")} placeholder="Mobile Login Logo" />
                        <Input {...getInputBrandInputValues("alternativeLogoHeight", { type: "number" })} placeholder="Logo height" />
                    </div>
                    <div className={styles["background-image-wrapper"]}>
                        <Input {...getInputBrandInputValues("backgroundImage")} placeholder="Background Image/Video" />
                        <Input {...getInputBrandInputValues("backgroundImageOpacity", { type: "number" })} placeholder="Opacity" />
                        <Input {...getInputBrandInputValues("backgroundImageBlur", { type: "number" })} placeholder="Blur" />
                    </div>
                    <Input {...getInputBrandInputValues("startBackground")} placeholder="Frontpage background Image/Video" />
                    <Input {...getInputBrandInputValues("successBackground")} placeholder="Success background Image/Video" />
                    <TurnrDropdownSelect {...getInputBrandInputValues("orderIdText")} fullWidth brandData={brandData} label="Order Id label" options={getOrderIdLabelIds()} />
                    <TurnrDropdownSelect {...getInputBrandInputValues("successConfetti")} fullWidth brandData={brandData} label="Success confetti" options={SUCCESS_CONFETTI_OPTIONS} />
                    <TurnrDropdownSelect {...getInputBrandInputValues("productsBundleMock")} fullWidth brandData={brandData} label={"Products"} options={Object.keys(productsBundleMocks)} />
                </div>
            </CollapsibleContent>
        </div>
    );
}
